<template>
	<div class="case-grid">

		<div class="case-grid-2-col-layout">
			<div class="tab-container">
				<div 
					class="tab"
					:class="[
						`${activeTab == 'details' ? 'active' : ''}`
					]"
					@click="switchTab('details')"
					>
					<div class="tab-label">
						Case Details
					</div>
				</div>

				<div 
					class="tab files-tab"
					:class="[
						`${activeTab == 'files' ? 'active' : ''}`
					]"
					@click="switchTab('files')"
					>
					<div class="tab-label">
						Files 
						<div 
							v-if="countFiles > 0"
							class="tab-item-count"
							>
							{{ countFiles }}
						</div>
					</div>
				</div>

				<div 
					class="tab forms-tab"
					:class="[
						`${activeTab == 'forms' ? 'active' : ''}`
					]"
					@click="switchTab('forms')"
					>
					<div class="tab-label">
						Forms
						<div 
							v-if="countDueForms > 0"
							class="tab-item-count color-red"
							>
							{{ countDueForms }}
						</div>
					</div>
				</div>

				<div 
					class="tab messages-tab"
					:class="[
						`${activeTab == 'messages' ? 'active' : ''}`
					]"
					@click="switchTab('messages')"
					>
					<div class="tab-label">
						Messages 
						<div 
							v-if="chat.length > 0"
							class="tab-item-count"
							>
							{{ chat.length }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="case-grid-2-col-layout">

			<div 
				class="details-column"
				:class="[
					`${['details','files','forms'].includes(activeTab) ? 'active' : ''}`
				]"
				>

				<div 
					v-if="activeTab == 'details'"
					class="case-details-container"
					>
					<Subtitle
						title="Case File"
						:subtext="$page.props.user.id == thisCase.user_id ? 'Main details of your case' : 'Main details of this case'"
						/>

					<div class="case-details-block">
						<table>
							<tr>
								<td>Status</td>
								<td>
									<div 
										class="status-indicator"
										:class="[
											`status-${thisCase.status}`
										]"
										>
										{{ prettyStatus(thisCase.status) }}
									</div>
								</td>
							</tr>
							<tr>
								<td>Created</td>
								<td>{{ formatDate(thisCase.created_at) }}</td>
							</tr>
							<tr>
								<td>Last Updated</td>
								<td>{{ formatDate(thisCase.updated_at) }}</td>
							</tr>
							<tr>
								<td>Surgeon</td>
								<td>{{ projectUser.name }} <small v-if="isAdmin">({{ projectUser.email }})</small></td>
							</tr>
							<tr>
								<td>Case ID</td>
								<td>{{ hashid }}</td>
							</tr>
							<tr>
								<td>Patient Reference</td>
								<td>{{ thisCase.patient_reference ? thisCase.patient_reference : 'N/A' }}</td>
							</tr>
							<tr>
								<td>Order Number</td>
								<td><strong>{{ thisCase.enquiry_number ? thisCase.enquiry_number : 'N/A' }}</strong></td>
							</tr>
							<tr>
								<td>Distributor</td>
								<td>{{ thisCase.teams.length > 0 ? thisCase.teams[0].name : 'Not assigned to a distributor' }}</td>
							</tr>
							<tr>
								<td>Description</td>
								<td>{{ thisCase.description }}</td>
							</tr>
							
							<tr v-if="thisCase.design_meeting_details != null">
								<td>Design Meeting Details</td>
								<td v-html="linkify(thisCase.design_meeting_details)" />
							</tr>

							<tr v-if="thisCase.treatment_plan_deadline != null && thisCase.treatment_plan_deadline != ''">
								<td>Treatment Plan Deadline</td>
								<td>
									{{ formatDate(thisCase.treatment_plan_deadline) }}
								</td>
							</tr>

							<tr v-if="thisCase.treatment_plan_approved != null">
								<td>Treatment Plan Status</td>
								<td>
									<div 
										class="status-indicator"
										:class="[
											`status-${thisCase.treatment_plan_approved}`
										]"
										>
										{{ prettyStatus(thisCase.treatment_plan_approved) }}
									</div>
								</td>
							</tr>

							<tr v-if="thisCase.implant_production_date != null && thisCase.implant_production_date != ''">
								<td>Production Date</td>
								<td>
									{{ formatDate(thisCase.implant_production_date) }}
								</td>
							</tr>

							<tr v-if="thisCase.surgery_date != null && thisCase.surgery_date != ''">
								<td>Surgery Date</td>
								<td>
									{{ formatDate(thisCase.surgery_date) }}
								</td>
							</tr>

							<tr v-if="thisCase.manager">
								<td>Rep</td>
								<td>{{ thisCase.manager.name }} <small v-if="isAdmin">({{ thisCase.manager.email }})</small></td>
							</tr>
							<tr v-if="thisCase.viewers && thisCase.viewers.length > 0">
								<td>Viewers</td>
								<td>
									<ul>
										<li v-for="viewer in thisCase.viewers" :key="viewer.id">
											{{ viewer.name }} <small v-if="isAdmin">({{ viewer.email }})</small>
										</li>
									</ul>
								</td>
							</tr>
							<tr v-if="thisCase.editors && thisCase.editors.length > 0">
								<td>Editors</td>
								<td>
									<ul>
										<li v-for="editor in thisCase.editors" :key="editor.id">
											{{ editor.name }} <small v-if="isAdmin">({{ editor.email }})</small>
										</li>
									</ul>
								</td>
							</tr>

						</table>
					</div>
				</div>

				<div 
					v-if="activeTab == 'files'"
					class="attached-files"
					>

					<Placeholder
						v-if="countFiles == 0"
						message="There are no files attached to this case."
						/>

					<Subtitle
						title="Enquiry File"
						v-if="enquiries.length > 0"
						/>
					<div class="file-list" v-if="enquiries.length > 0">
						<FileBox
							v-for="(enquiry, index) in enquiries"
							:key="index"
							:file="enquiry"
							:admin="true"
							:canDownload="true"
							/>
					</div>

					<Subtitle
						title="Image Files"
						v-if="images.length > 0"
						/>
					<div class="file-list" v-if="images.length > 0">
						<FileBox
							v-for="(image, index) in images"
							:key="index"
							:file="image"
							:admin="true"
							:canDownload="true"
							/>
					</div>

					<Subtitle
						title="Treatment Plan"
						v-if="treatmentPlans.length > 0"
						/>
					<div class="file-list" v-if="treatmentPlans.length > 0">
						<FileBox
							v-for="(plan, index) in treatmentPlans"
							:key="index"
							:file="plan"
							:canDownload="true"
							/>
					</div>

					<Subtitle
						title="Instructions for use"
						v-if="ifus.length > 0"
						/>
					<div class="file-list" v-if="ifus.length > 0">
						<FileBox
							v-for="(ifu, index) in ifus"
							:key="index"
							:file="ifu"
							:canDownload="true"
							/>
					</div>
				</div>

				<div 
					v-if="activeTab == 'forms'"
					class="pms-forms"
					>

					<div 
						v-if="filledFormsCount > 0"
						class="past-form-submissions"
						>
						<Subtitle
							v-if="filledFormsCount > 0 && thisCase.progress_stage < 5 && (!forms.onceBefore || forms.onceBefore.length == 0)"
							title="Consent forms"
							subtext="View your patient consent forms"
							/>

						<div class="button-bar">
							<Button
								v-if="filledFormsCount > 0"
								label="View form submissions"
								color="blue"
								icon="list"
								:buttonRoute="`/cases/${thisCase.hashid}/form-submissions`"
								:fullWidth="true"
								/>
						</div>
					</div>

					<div 
						v-if="forms.onceBefore && forms.onceBefore.length > 0"
						class="pms-forms-inner" 
						>
						<Subtitle
							title="Consent forms"
							subtext="Please fill out these patient consent forms"
							/>
						
						<div 
							class="form-group"
							>
							<div class="form-group-title">
								Due Forms
							</div>

							<template v-if="forms.onceBefore && forms.onceBefore.length > 0">
								<div 
									v-for="(form, index) in forms.onceBefore"
									:key="index"
									class="form-row"
									>
									<div class="form-details">
										<div class="form-row-title">
											{{ form.name }}
										</div>
										<div class="form-row-due-date">
											Due: {{ formatTimepoint(form) }}
										</div>
									</div>
									<div 
										v-if="permissions.canCompleteForms"
										class="actions"
										>
										<template v-if="form.hasDraft && form.formResultID">
											<Button
												label="Continue"
												color="blue"
												icon="pencil"
												:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}?r=${form.formResultID}`"
												/>
										</template>
										<Button
											v-else
											:label="form.upload ? `Upload` : `Fill`"
											color="green"
											:icon="form.upload ? `upload` : `pencil`"
											:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}`"
											/>
									</div>
								</div>
							</template>

						</div>
					</div>

					<div 
						v-if="thisCase.progress_stage == 5"
						class="pms-forms-inner" 
						>
						<Subtitle
							title="Post Market Surveillance"
							subtext="Please complete the following forms to submit your PMCF data"
							/>
						
						<div
							v-if="(forms.onceAfter && forms.onceAfter.length > 0) || (forms.timePoints && forms.timePoints.length > 0)" 
							class="form-group"
							>
							<div class="form-group-title">
								Due Forms
							</div>

							<template v-if="forms.onceAfter && forms.onceAfter.length > 0">
								<div 
									v-for="(form, index) in forms.onceAfter"
									:key="index"
									class="form-row"
									>
									<div class="form-details">
										<div class="form-row-title">
											{{ form.name }}
										</div>
										<div class="form-row-due-date">
											Due: {{ formatTimepoint(form) }}
										</div>
									</div>
									<div 
										v-if="permissions.canCompleteForms"
										class="actions"
										>
										<template v-if="form.hasDraft && form.formResultID">
											<Button
												label="Continue"
												color="blue"
												icon="pencil"
												:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}?r=${form.formResultID}`"
												/>
										</template>
										<Button
											v-else
											:label="form.upload ? `Upload` : `Fill`"
											color="green"
											:icon="form.upload ? `upload` : `pencil`"
											:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}`"
											/>
									</div>
								</div>
							</template>

							<template v-if="forms.timePoints && forms.timePoints.length > 0">
								<template 
									v-for="(form, index) in forms.timePoints"
									:key="index"
									>
									<div
										v-if="!form.due_date_in_future"
										class="form-row"
										>
										<div class="form-details">
											<div class="form-row-title">
												{{ form.name }}
											</div>
											<div class="form-row-due-date">
												Due: {{ formatTimepoint(form) }} ({{ form.week }} Week)
											</div>
										</div>
										<div 
											v-if="permissions.canCompleteForms"
											class="actions"
											>
											<template v-if="form.hasDraft && form.formResultID">
												<Button
													label="Continue"
													color="blue"
													icon="pencil"
													:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}?r=${form.formResultID}&week=${form.week}`"
													/>
											</template>
											<Button
												v-else
												:label="form.upload ? `Upload` : `Fill`"
												color="green"
												:icon="form.upload ? `upload` : `pencil`"
												:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}?week=${form.week}`"
												/>
										</div>
									</div>
								</template>
							</template>
						</div>

						<Placeholder
							v-else
							message="There are no forms currently due to be completed."
							/>

						<div
							v-if="forms.trigger.length > 0" 
							class="form-group"
							>
							<div class="form-group-title">
								Ad-hoc Forms
							</div>

							<div 
								v-for="(form, index) in forms.trigger"
								:key="index"
								class="form-row"
								>
								<div class="form-details">
									<div class="form-row-title">
										{{ form.name }}
									</div>
									<div class="form-row-due-date">
										Please fill when required
									</div>
								</div>
								<div 
									v-if="permissions.canCompleteForms"
									class="actions"
									>
									<Button
										:label="form.upload ? `Upload` : `Fill`"
										color="green"
										:icon="form.upload ? `upload` : `pencil`"
										:buttonRoute="`/cases/${thisCase.hashid}/form/${form.id}`"
										/>
								</div>
							</div>
						</div>

					</div>

				</div>

			</div>
			<div 
				class="messages-column"
				:class="[
					`${activeTab == 'messages' ? 'active' : ''}`
				]"
				>
				<Subtitle
					title="Messages"
					subtext="Correspondence between you and the client"
					/>

				<Messages 
					:thisCase="thisCase"
					:chat="chat"
					:userId="userId"
					:isAdmin="isAdmin"
					:projectUser="projectUser"
					:permissions="permissions"
					/>

			</div>

		</div>

	</div>
</template>

<script>

import moment from 'moment'

import Subtitle from "@/Components/Subtitle"
import Messages from "@/Components/Cases/Messages"
import FileBox from "@/Components/Cases/FileBox"
import Button from "@/Components/Button"
import ButtonDownload from "@/Components/ButtonDownload"
import Placeholder from "@/Components/Placeholder"

export default {
	components: {
		Subtitle,
		Messages,
		FileBox,
		Button,
		ButtonDownload,
		Placeholder
	},
	props: {
		thisCase: {
			type: Object,
			required: true
		},
		chat: {
			type: Array,
			required: true
		},
		userId: {
			type: Number,
			required: true
		},
		projectUser: {
			type: Object,
			required: true
		},
		permissions: {
			type: Object,
			required: true
		},
		treatmentPlans: {
			type: Array,
			required: true
		},
		ifus: {
			type: Array,
			required: false
		},
		images: {
			type: Array,
			required: true
		},
		enquiries: {
			type: Array,
			required: true
		},
		hashid: {
			type: String,
			required: true
		},
		isAdmin: {
			type: Boolean,
			required: true
		},
		forms: {
			type: [Array, Boolean],
			required: false,
			default: false
		},
		filledFormsCount: {
			type: Number,
			required: false,
			default: 0
		}
	},
	data: () => {
		return {
			activeTab: 'details'
		}
	},
	mounted(){
		// console.log('forms', this.forms)
	},
	methods:{
		formatTimepoint(form){

			// console.log('formatTimepoint', form)
			
			if(form.due_date){
				return moment(form.due_date).format('Do MMMM YYYY')
			}else{
				let timepoint = form.timepoint

				if(timepoint.mode == 'once-before-pms'){
					return 'On creation of a case'
				}else if(timepoint.mode == 'once-after-surgery'){
					return 'Immediately after surgery'
				}else if(timepoint.mode == 'user-triggered'){
					return `User triggered`
				}else if(timepoint.mode == 'time-intervals'){

					if(this.thisCase.surgery_date != null){
						
						return `Due some time after the surgery date`
						
					}else{
						return 'Time intervals from surgery date (not set)'
					}
				}
			}
		},
		switchTab(tab){
			this.activeTab = tab
		},
		formatDate(inputDate) {
      if(inputDate != null){
        return moment(inputDate).format('Do MMMM YYYY')
      }
    },
    prettyStatus(status){
      if(status == 'review'){
        return 'In Review'
      }else if(status == 'pending'){
        return 'Pending Enquiry Upload'
      }else if(status == 'accepted'){
        return 'Accepted'
      }else if(status == 'approved'){
        return 'Approved'
      }else if(status == 'archive'){
        return 'Archived'
      }else if(status == 'rejected'){
        return 'Rejected'
      }
    },
    linkify(inputText) {
      let replacedText, replacePattern1, replacePattern2, replacePattern3;

      //URLs starting with http://, https://, or ftp://
      replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
      replacedText = inputText.replace(replacePattern1, '<a href="$1" target="_blank">$1</a>');

      //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
      replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
      replacedText = replacedText.replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>');

      //Change email addresses to mailto:: links.
      replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
      replacedText = replacedText.replace(replacePattern3, '<a href="mailto:$1">$1</a>');

      return replacedText;
    }
	},
	computed: {
		countFiles(){
			return this.enquiries.length + this.images.length + this.treatmentPlans.length + this.ifus.length
		},
		countDueForms(){
			let timePointForms = this.forms.timePoints ? this.forms.timePoints : []
			// go through each timepoint form and check if it's due_date_in_future is false. If it is, add it to the count
			let dueForms = timePointForms.filter(form => !form.due_date_in_future)

			if(this.thisCase.progress_stage < 5){
				return this.forms.onceBefore.length
			}else{
				return this.forms.onceBefore.length + this.forms.onceAfter.length + dueForms.length
			}
		}
	}
}

</script>

<style lang="sass">

.case-grid
	padding-top: 20px

	.case-grid-2-col-layout
		display: grid
		grid-template-columns: 1fr 0.7fr
		grid-column-gap: 25px

		@media (max-width: 1210px)
			grid-template-columns: 1fr 1fr

		@media (max-width: 980px)
			grid-template-columns: 1fr

	.button-bar
		display: flex
		gap: 10px

		@media (max-width: 980px)
			flex-direction: column

	.tab-container
		display: flex
		align-items: center
		justify-content: flex-start
		border-bottom: 1px solid rgba(#000, 0.1)
		margin-bottom: 30px
		overflow-y: hidden
		overflow-x: auto
		-webkit-overflow-scrolling: touch
		
		// make scroll bar invisible
		&::-webkit-scrollbar
			display: none


		@media (max-width: 982px)
			margin: 0 -20px 20px
			padding: 0 20px

		.tab
			padding: 10px 20px
			cursor: pointer
			transition: all 300ms
			// border-right: 1px solid rgba(#000, 0.1)
			font-weight: 700
			background-color: #fff
			box-shadow: 0 1px 3px rgba(#000, 0.2)
			white-space: nowrap
			user-select: none

			.tab-label
				display: flex
				align-items: center
				gap: 8px

			.tab-item-count
				background-color: #70c2ff
				color: #fff
				border-radius: 12px
				width: auto
				padding: 0 5px
				height: 20px
				display: flex
				align-items: center
				justify-content: center
				font-size: 12px
				font-weight: 700
				letter-spacing: -1px

				&.color-red
					background-color: #d00
					color: #fff

			&:first-child
				border-radius: 8px 0 0 0

			&:last-child
				border-radius: 0 8px 0 0
			
			&.forms-tab
				border-radius: 0 8px 0 0

				@media (max-width: 982px)
					border-radius: 0

			&.active
				background-color: #70c2ff
				color: #fff

				.tab-item-count
					background-color: #fff
					color: #70c2ff

		@media (min-width: 981px)
			.tab
				&.messages-tab
					display: none

	.details-column
		@media (max-width: 980px)
			display: none

			&.active
				display: block

		.pms-forms
			margin-bottom: 30px

			.past-form-submissions
				margin-bottom: 30px

		.form-group
			display: flex
			flex-direction: column
			gap: 10px
			margin-bottom: 25px

			.form-group-title
				font-size: 18px
				font-weight: 700
				color: #252525
				margin-bottom: 5px
				font-family: 'Roboto Condensed', sans-serif

			.form-row
				display: flex
				align-items: center
				justify-content: space-between
				padding: 12px 15px
				border-radius: 8px
				box-shadow: 0 1px 3px rgba(#000, 0.2)
				background-color: #fff

				.form-details
					display: flex
					flex-direction: column
					gap: 2px

					.form-row-title
						font-size: 16px
						font-weight: 700
						color: #252525
						font-family: 'Roboto Condensed', sans-serif

					.form-row-due-date
						font-size: 14px
						font-weight: 400
						color: #666

				.actions
					display: flex
					align-items: center
					gap: 10px

		.case-details-block
			width: 100%
			height: auto
			border-radius: 8px
			margin-bottom: 40px
			box-shadow: 0 1px 3px rgba(#000, 0.2)
			background-color: #fff
			overflow: hidden
			position: relative

			.status-indicator
				font-weight: 700
				position: relative
				display: flex
				align-items: center

				&:before
					content: ''
					width: 11px
					height: 11px
					margin-right: 10px
					border-radius: 50%

				&.status-review, &.status-pending
					color: #368BCB

					&:before
						background-color: #368BCB

				&.status-accepted, &.status-approved
					color: #59AD4B

					&:before
						background-color: #59AD4B

				&.status-rejected
					color: #f00

					&:before
						background-color: #f00

				&.status-archive
					color: #777

					&:before
						background-color: #777

			table
				width: 100%

				td
					padding: 12px 30px

					&:first-child
						width: 30%
						border-right: 1px solid rgba(#000, 0.1)
						text-align: right
						font-family: 'Roboto Condensed', sans-serif
						color: #252525
						font-size: 18px
						font-weight: 700
						line-height: 1.2

					&:last-child
						font-family: 'Roboto', sans-serif
						color: #666
						font-size: 16px
						font-weight: 400
				
				tr

					&:first-child
						td
							padding-top: 30px

					&:last-child
						td
							padding-bottom: 30px

					@media (max-width: 768px)
						display: flex
						flex-direction: column
						border-bottom: 1px solid rgba(#000, 0.1)

						td
							width: 100%
							padding: 8px 15px

							&:first-child
								border-right: none
								text-align: left
								padding-top: 15px
								width: 100%

							&:last-child
								padding-top: 0
								padding-bottom: 15px

						&:first-child
							padding-top: 10px

							td
								padding-top: 8px

						&:last-child
							padding-bottom: 10px
							
							td
								padding-bottom: 8px

		.file-list
			margin-bottom: 40px
			width: 100%
			display: flex
			flex-direction: column
			gap: 10px

	.messages-column
		@media (max-width: 980px)
			display: none

			&.active
				display: block

</style>